/**
 * Intent v1 — parser.
 *
 * `parseIntent` is *total*: it never throws. Every failure path returns typed errors,
 * because the parser runs on user-edited text from the web review phase and on
 * agent-drafted text from `/spectree:bug-to-er --draft-intent`.
 *
 * The frontmatter is parsed by hand — `packages/shared` carries no YAML dependency and
 * must not gain one.
 */
import { INTENT_KINDS, INTENT_SCHEMA_VERSION, INTENT_SOURCES, PLAYBOOK_SECTIONS, PLAYBOOK_SECTION_TITLES, } from "./types.js";
import { sectionForTitle, slotIdForLabel, SLOT_REGISTRY } from "./slots.js";
import { INTENT_FRONTMATTER_FENCE, INTENT_SOURCE_PREFIX, INTENT_UNKNOWN_MARKER } from "./format.js";
/**
 * Frontmatter keys the parser recognises. The frontmatter is parsed by hand —
 * `packages/shared` carries no YAML dependency and must not gain one — so this list is
 * a literal that has to be extended by hand for every new key. A key missing from here
 * is dropped with an `UNKNOWN_FRONTMATTER_KEY` warning, so forgetting to add one makes
 * every document carrying it warn.
 */
const KNOWN_FRONTMATTER_KEYS = new Set([
    "schemaVersion",
    "source",
    "kind",
    "createdAt",
    "reporterRole",
]);
/**
 * Parse an `intent.md` document.
 *
 * @param text Raw document text. CRLF line endings are normalised.
 * @returns `{ ok: true, intent }` or `{ ok: false, errors }` — never throws.
 */
export function parseIntent(text) {
    const errors = [];
    const warnings = [];
    if (typeof text !== "string" || text.trim().length === 0) {
        return {
            ok: false,
            errors: [
                {
                    code: "MISSING_FRONTMATTER",
                    message: "The document is empty, so it carries no Intent v1 frontmatter.",
                },
            ],
            warnings,
        };
    }
    const lines = text.replace(/\r\n/g, "\n").split("\n");
    const frontmatterBlock = extractFrontmatter(lines);
    if (!frontmatterBlock) {
        return {
            ok: false,
            errors: [
                {
                    code: "MISSING_FRONTMATTER",
                    message: "The document does not open with a '---' frontmatter fence, so schemaVersion cannot be read.",
                },
            ],
            warnings,
        };
    }
    const rawFrontmatter = parseFrontmatterLines(frontmatterBlock.lines, warnings);
    const schemaVersion = rawFrontmatter.get("schemaVersion");
    if (schemaVersion === undefined) {
        errors.push({
            code: "MISSING_FRONTMATTER",
            message: "Frontmatter is missing the required key 'schemaVersion'.",
            field: "schemaVersion",
        });
    }
    else if (schemaVersion !== INTENT_SCHEMA_VERSION) {
        errors.push({
            code: "UNKNOWN_SCHEMA_VERSION",
            message: `Unsupported schemaVersion '${schemaVersion}'. This parser understands '${INTENT_SCHEMA_VERSION}' only.`,
            field: "schemaVersion",
            found: schemaVersion,
        });
    }
    const source = rawFrontmatter.get("source");
    if (source === undefined) {
        errors.push({
            code: "MISSING_FRONTMATTER",
            message: "Frontmatter is missing the required key 'source'.",
            field: "source",
        });
    }
    else if (!isIntentSource(source)) {
        errors.push({
            code: "MISSING_FRONTMATTER",
            message: `Frontmatter key 'source' has unrecognised value '${source}'. Expected one of: ${INTENT_SOURCES.join(", ")}.`,
            field: "source",
            found: source,
        });
    }
    const createdAt = rawFrontmatter.get("createdAt");
    if (createdAt === undefined || createdAt.length === 0) {
        errors.push({
            code: "MISSING_FRONTMATTER",
            message: "Frontmatter is missing the required key 'createdAt'.",
            field: "createdAt",
        });
    }
    const sections = splitSections(lines.slice(frontmatterBlock.endIndex + 1), warnings);
    for (const section of PLAYBOOK_SECTIONS) {
        if (!sections.has(section)) {
            errors.push({
                code: "MISSING_SECTION",
                message: `The document is missing the required section '## ${PLAYBOOK_SECTION_TITLES[section]}'.`,
                section,
            });
        }
    }
    const slots = {};
    for (const [, sectionLines] of sections) {
        for (const block of splitSlotBlocks(sectionLines)) {
            const slotId = slotIdForLabel(block.label);
            if (slotId === undefined) {
                warnings.push({
                    code: "UNKNOWN_HEADING",
                    message: `Ignoring unrecognised slot heading '### ${block.label}'. No value was inferred from it.`,
                    label: block.label,
                });
                continue;
            }
            const value = parseSlotBlock(slotId, block, errors, warnings);
            if (value !== undefined) {
                slots[slotId] = value;
            }
        }
    }
    if (errors.length > 0) {
        return { ok: false, errors, warnings };
    }
    // Re-narrow the frontmatter values. Every one of these was validated above, so a
    // failure here is unreachable; it is written as a guard rather than an assertion so
    // the parser stays free of non-null assertions.
    if (source === undefined || !isIntentSource(source) || createdAt === undefined) {
        return {
            ok: false,
            errors: [
                {
                    code: "MISSING_FRONTMATTER",
                    message: "Frontmatter is missing a required key.",
                },
            ],
            warnings,
        };
    }
    const kind = parseKind(rawFrontmatter.get("kind"), warnings);
    const reporterRole = rawFrontmatter.get("reporterRole");
    const frontmatter = {
        schemaVersion: INTENT_SCHEMA_VERSION,
        source,
        ...(kind !== undefined ? { kind } : {}),
        createdAt,
        ...(reporterRole !== undefined && reporterRole.length > 0 ? { reporterRole } : {}),
    };
    return { ok: true, intent: { frontmatter, slots }, warnings };
}
function isIntentSource(value) {
    return INTENT_SOURCES.includes(value);
}
function isIntentKind(value) {
    return INTENT_KINDS.includes(value);
}
/**
 * Read the optional `kind` frontmatter value.
 *
 * The kind is never required and never fatal. An absent key parses silently — that is
 * every document written before the kind existed, and it means `bug`. A present but
 * unrecognised value warns and is dropped, so `resolveIntentKind` falls back to `bug`
 * rather than the caller inheriting a value no selector can resolve.
 *
 * @param raw The raw frontmatter value, or `undefined` when the key is absent.
 * @param warnings Warning sink; appended to for an unrecognised value only.
 * @returns The recognised kind, or `undefined` to leave the field off the frontmatter.
 */
function parseKind(raw, warnings) {
    if (raw === undefined || raw.length === 0) {
        return undefined;
    }
    if (isIntentKind(raw)) {
        return raw;
    }
    warnings.push({
        code: "UNKNOWN_KIND",
        message: `Ignoring unrecognised frontmatter kind '${raw}'. Expected one of: ${INTENT_KINDS.join(", ")}. Treating this document as a bug report.`,
        label: raw,
    });
    return undefined;
}
function extractFrontmatter(lines) {
    let start = 0;
    while (start < lines.length && (lines[start] ?? "").trim().length === 0) {
        start += 1;
    }
    if ((lines[start] ?? "").trim() !== INTENT_FRONTMATTER_FENCE) {
        return undefined;
    }
    for (let i = start + 1; i < lines.length; i += 1) {
        if ((lines[i] ?? "").trim() === INTENT_FRONTMATTER_FENCE) {
            return { lines: lines.slice(start + 1, i), endIndex: i };
        }
    }
    return undefined;
}
function parseFrontmatterLines(lines, warnings) {
    const entries = new Map();
    for (const raw of lines) {
        const line = raw.trim();
        if (line.length === 0 || line.startsWith("#")) {
            continue;
        }
        const separator = line.indexOf(":");
        if (separator === -1) {
            warnings.push({
                code: "UNKNOWN_FRONTMATTER_KEY",
                message: `Ignoring frontmatter line without a ':' separator: '${line}'.`,
                label: line,
            });
            continue;
        }
        const key = line.slice(0, separator).trim();
        const value = stripQuotes(line.slice(separator + 1).trim());
        if (!KNOWN_FRONTMATTER_KEYS.has(key)) {
            warnings.push({
                code: "UNKNOWN_FRONTMATTER_KEY",
                message: `Ignoring unrecognised frontmatter key '${key}'.`,
                label: key,
            });
            continue;
        }
        entries.set(key, value);
    }
    return entries;
}
function stripQuotes(value) {
    if (value.length >= 2) {
        const first = value[0];
        const last = value[value.length - 1];
        if ((first === '"' && last === '"') || (first === "'" && last === "'")) {
            return value.slice(1, -1);
        }
    }
    return value;
}
/** Group the body into level-2 playbook sections, ignoring anything before the first one. */
function splitSections(lines, warnings) {
    const sections = new Map();
    let current;
    for (const raw of lines) {
        const heading = matchHeading(raw, 2);
        if (heading !== undefined) {
            const section = sectionForTitle(heading);
            if (section === undefined) {
                warnings.push({
                    code: "UNKNOWN_HEADING",
                    message: `Ignoring unrecognised section heading '## ${heading}'.`,
                    label: heading,
                });
                current = undefined;
                continue;
            }
            current = sections.get(section) ?? [];
            sections.set(section, current);
            continue;
        }
        current?.push(raw);
    }
    return sections;
}
/** Group one section's lines into level-3 slot blocks. */
function splitSlotBlocks(lines) {
    const blocks = [];
    let current;
    for (const raw of lines) {
        const heading = matchHeading(raw, 3);
        if (heading !== undefined) {
            current = { label: heading, lines: [] };
            blocks.push(current);
            continue;
        }
        current?.lines.push(raw);
    }
    return blocks;
}
function matchHeading(line, level) {
    const prefix = `${"#".repeat(level)} `;
    // `prefix` ends in a space, so '#### x' never matches level 3.
    if (!line.startsWith(prefix)) {
        return undefined;
    }
    return line.slice(prefix.length).trim();
}
function parseSlotBlock(slotId, block, errors, warnings) {
    const body = trimBlankEdges(block.lines);
    if (body.length === 0) {
        warnings.push({
            code: "EMPTY_SLOT",
            message: `Slot '${SLOT_REGISTRY[slotId].label}' has no content, so it is treated as unanswered rather than filled.`,
            slotId,
        });
        return undefined;
    }
    if ((body[0] ?? "").trim().toLowerCase() === INTENT_UNKNOWN_MARKER) {
        const question = readBlockquote(body.slice(1));
        if (question.length === 0) {
            errors.push({
                code: "UNPARSEABLE_SLOT",
                message: `Slot '${SLOT_REGISTRY[slotId].label}' is marked unknown but carries no question for a human to answer.`,
                slotId,
            });
            return undefined;
        }
        return { state: "unknown", question };
    }
    let valueLines = [...body];
    let source;
    const last = (valueLines[valueLines.length - 1] ?? "").trim();
    if (last.toLowerCase().startsWith(INTENT_SOURCE_PREFIX)) {
        source = last.slice(INTENT_SOURCE_PREFIX.length).trim();
        valueLines = trimBlankEdges(valueLines.slice(0, -1));
    }
    const value = valueLines.join("\n").trim();
    if (value.length === 0) {
        errors.push({
            code: "UNPARSEABLE_SLOT",
            message: `Slot '${SLOT_REGISTRY[slotId].label}' carries a source pointer but no value.`,
            slotId,
        });
        return undefined;
    }
    return source !== undefined && source.length > 0
        ? { state: "filled", value, source }
        : { state: "filled", value };
}
/** Read a `> `-prefixed blockquote, joining its lines with newlines. */
function readBlockquote(lines) {
    const quoted = [];
    for (const raw of lines) {
        const line = raw.trim();
        if (line.length === 0) {
            continue;
        }
        if (!line.startsWith(">")) {
            continue;
        }
        quoted.push(line.replace(/^>\s?/, ""));
    }
    return quoted.join("\n").trim();
}
function trimBlankEdges(lines) {
    const copy = [...lines];
    while (copy.length > 0 && (copy[0] ?? "").trim().length === 0) {
        copy.shift();
    }
    while (copy.length > 0 && (copy[copy.length - 1] ?? "").trim().length === 0) {
        copy.pop();
    }
    return copy;
}
//# sourceMappingURL=parse.js.map